import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Auth } from 'aws-amplify';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { publicURL } from '../../../../../utils/axiosInstance';
import { ReactComponent as FacebookSVG } from '../../../../../assets/login/f.svg';
import { ReactComponent as GoogleSVG } from '../../../../../assets/login/G.svg';
import PhoneInputControlled from '../../../PhoneInputControlled/PhoneInputControlled';
import TermsOfUse from '../../../../pages/TermsOfUse/TermsOfUse';
import PrivacyNotice from '../../../../pages/PrivacyNotice/PrivacyNotice';
import cogoToast from 'cogo-toast';
import { toast } from '../../../../toaster/toast';
import { connect } from 'react-redux';
import { CountryInfo } from '../../../../../redux/countryInfo/countryInfo';

interface SignUpProps {
  onFacebookSubmit: Function;
  onGoogleSubmit: Function;
  changeToLogin: Function;
  changeToConfirm: Function;
  setUsername: Function;
  hideSignInModal: Function;
  countryInfo: CountryInfo;
}

function SignUp({ onFacebookSubmit, onGoogleSubmit, changeToLogin, changeToConfirm, setUsername, hideSignInModal, countryInfo }: SignUpProps) {
  const {
    register,
    handleSubmit,
    getValues,
    control,
    formState: { errors }
  } = useForm();
  const [show, setShow] = useState(false);
  const [content, setContent] = useState('');

  const handleClose = () => setShow(false);
  const handleShow = (content: string) => {
    setContent(content);
    setShow(true);
  };

  async function signUp(data: any) {
    try {
      await Auth.signUp({
        username: data.email,
        password: data.password,
        attributes: {
          given_name: data.firstName,
          family_name: data.lastName,
          phone_number: `+${data.phone}`
        }
      });
      setUsername(data.email);
      cogoToast.success('User successfully created, please confirm your user.');
      changeToConfirm();
    } catch (error: any) {
      toast.error(error.message);
      console.log('error signing up:', error);
    }
  }

  return (
    <>
      <Col xs={12} lg={6} className="p-3 py-sm-0 px-sm-5  gray-background-sign">
        <div>
          <button className="close" onClick={() => hideSignInModal()}>
            &times;
          </button>
        </div>
        <div className="sign-up-header">Sign Up</div>
        <div>
          <Form onSubmit={handleSubmit(signUp)}>
            <Form.Row>
              <Form.Group as={Col} controlId="formName" className={'mb-2'}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  placeholder="Name"
                  {...register('firstName', {
                    required: 'First name required'
                  })}
                />
                {errors.firstName && (
                  <Form.Control.Feedback type="invalid" className="display-block">
                    {errors.firstName.message || 'Error'}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group as={Col} controlId="formLastName" className={'mb-2'}>
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  placeholder="Last Name"
                  {...register('lastName', {
                    required: 'Last name required'
                  })}
                />
                {errors.lastName && (
                  <Form.Control.Feedback type="invalid" className="display-block">
                    {errors.lastName.message || 'Error'}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} controlId="formEmail" className={'mb-2'}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  {...register('email', {
                    required: 'Email required',
                    pattern: {
                      value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                      message: 'Email not valid.'
                    }
                  })}
                />
                {errors.email && (
                  <Form.Control.Feedback type="invalid" className="display-block">
                    {errors.email.message || 'Error'}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId="formPhone" className="phone-input mb-2">
                <Form.Label>Phone</Form.Label>
                <PhoneInputControlled control={control} name={'phone'} defaultCountry={countryInfo?.country_code?.toLowerCase()} />
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId="formPassword" className={'mb-2'} xs={12} sm={6}>
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Password"
                  {...register('password', {
                    required: 'Password required',
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-“!@#%&^*$/,><\’:;|_~`])\S{8,99}$/,
                      message:
                        'The password must contain at least one lowercase alphabetical character, one uppercase alphabetical character, one special character and must be min 8 characters.'
                    }
                  })}
                />
                {errors.password && (
                  <Form.Control.Feedback type="invalid" className="display-block">
                    {errors.password.message || 'Error'}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group as={Col} controlId="formPhone" className={'mb-2'} xs={12} sm={6}>
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="ConfirmPassword"
                  {...register('confirm_password', {
                    validate: value => value === getValues('password') || "Passwords don't match"
                  })}
                />
                {errors.confirm_password && (
                  <Form.Control.Feedback type="invalid" className="display-block">
                    {errors.confirm_password.message || 'Error'}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              {!errors.password && (
                <Form.Text id="passwordHelpText" className={'passwordHelpText'}>
                  Password must contain at least one uppercase letter, one lowercase letter, one number and one special character (!@#$%^&*)
                </Form.Text>
              )}
            </Form.Row>

            <Form.Row className="center-content align-baseline check-agreement my-2">
              <Form.Label className="label-terms mb-0">
                <Form.Check
                  type="checkbox"
                  id="work-trip-switch"
                  {...register('terms', {
                    required: 'You must accept terms to continue.'
                  })}
                />
              </Form.Label>
              <span className="terms-agreement">
                I agree with Urbain{' '}
                <span className="terms-agreement-blue">
                  <span className="pointer" onClick={() => handleShow('terms')}>
                    Terms of Use
                  </span>{' '}
                  &{' '}
                  <span className="pointer" onClick={() => handleShow('privacy')}>
                    Privacy Policy
                  </span>
                  <Modal show={show} onHide={handleClose} className="font-lato" size="lg">
                    <Modal.Body>
                      {content && content === 'terms' && <TermsOfUse />}
                      {content && content === 'privacy' && <PrivacyNotice />}
                    </Modal.Body>
                  </Modal>
                </span>
              </span>

              {errors.terms && (
                <Form.Control.Feedback type="invalid" className="display-block pb-2">
                  {errors.terms.message || 'Error'}
                </Form.Control.Feedback>
              )}
            </Form.Row>

            <Button variant="dark" type="submit" className="create-account">
              Create Account
            </Button>

            <Form.Row className="center-content pt-3 pb-2">
              <Form.Text id="log-in-link">
                Have an account?{' '}
                <span className="underline pointer" onClick={() => changeToLogin()}>
                  Log In
                </span>
              </Form.Text>
            </Form.Row>

            <Form.Row className="center-content pb-3">
              <Form.Text id="log-in-link">
                Already have a code?{' '}
                <span className="underline pointer" onClick={() => changeToConfirm()}>
                  Confirm Account
                </span>
              </Form.Text>
            </Form.Row>

            <Form.Row className="pb-5 d-flex federation-buttons">
              <Button onClick={() => onFacebookSubmit()} className="facebook-button">
                <FacebookSVG className="facebook-button-icon" />
                <span>Facebook</span>
              </Button>
              <Button onClick={() => onGoogleSubmit()} className="google-button">
                <GoogleSVG className="google-button-icon" />
                <span className={'google-span'}>Google</span>
              </Button>
            </Form.Row>
          </Form>
        </div>
      </Col>
      <Col xs={0} lg={6} className="d-none d-lg-block p-0">
        <Row className="m-0">
        <LazyLoadImage src={`${publicURL}/images/signInUp/young-hispanic-family-living-room-vacation.jpeg`} className="sing-up-img" />
          <div className="center-div">
            <div className="center-div-header">Stay Delighted.</div>
            <div className="center-div-sub-header">Become an Urbain Member.</div>
          </div>
          {/*<div className="center-div center-div-bottom">
            <div className="center-div-text">Enjoy 10% off your first booking.</div>
          </div> // Commented for MVP */}
        </Row>
      </Col>
    </>
  );
}

const mapStateToProps = (state: any) => ({
  countryInfo: state.countryInfo
});

export default connect(mapStateToProps)(SignUp);
